'use client';

import * as React from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import { cn } from './utils';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => <AccordionPrimitive.Item ref={ref} className={cn(className)} {...props} />);
AccordionItem.displayName = 'AccordionItem';

// Flexible icon type that works with lucide-react, custom SVGs, and other icon libraries
type IconComponent = React.ComponentType<{
  className?: string;
  size?: number;
  [key: string]: unknown;
}>;

type AccordionTriggerProps = {
  isActive?: boolean;
  hideIcon?: boolean;
  Icon?: IconComponent;
  iconProps?: { [key: string]: unknown };
  rotateIcon?: boolean;
} & React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>;

const AccordionTrigger = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Trigger>, AccordionTriggerProps>(
  ({ className, children, isActive, hideIcon, Icon, iconProps, rotateIcon = true, ...props }, ref) => {
    const IconComponent = Icon ?? ChevronDown;

    return (
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
          ref={ref}
          className={cn(
            'flex flex-1 items-center justify-between py-4 font-medium transition-all',
            '[&>span>svg]:transition-transform [&>span>svg]:duration-200',
            // CSS-only rotation fallback when isActive not provided (uses Radix data-state)
            typeof isActive === 'undefined' && rotateIcon !== false && '[&[data-state=open]>span>svg]:rotate-180',
            // Reduced motion: disable icon animation
            'motion-reduce:[&>span>svg]:transition-none',
            className,
          )}
          {...props}
        >
          {children}
          {!hideIcon && (
            <span>
              <IconComponent
                className={cn(
                  'h-4 w-4 shrink-0 text-text-secondary',
                  // Explicit rotation when isActive is provided
                  rotateIcon && isActive && 'rotate-180',
                )}
                {...iconProps}
              />
            </span>
          )}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    );
  },
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      'overflow-hidden transition-all',
      'motion-reduce:animate-none',
      'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
    )}
    {...props}
  >
    <div className={className}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

// Single-select wrapper
type SingleAccordionProps = {
  children: React.ReactNode;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string | undefined) => void;
  collapsible?: boolean;
  className?: string;
};

const SingleAccordion = ({ children, defaultValue, value, onValueChange, collapsible = true, className }: SingleAccordionProps) => (
  <Accordion
    type="single"
    collapsible={collapsible}
    defaultValue={defaultValue}
    value={value}
    onValueChange={onValueChange}
    className={cn('w-full', className)}
  >
    {children}
  </Accordion>
);

// Multi-select wrapper
type MultipleAccordionProps = {
  children: React.ReactNode;
  defaultValue?: string[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
  className?: string;
};

const MultipleAccordion = ({ children, defaultValue, value, onValueChange, className }: MultipleAccordionProps) => (
  <Accordion type="multiple" defaultValue={defaultValue} value={value} onValueChange={onValueChange} className={cn('w-full', className)}>
    {children}
  </Accordion>
);

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger, MultipleAccordion, SingleAccordion };
export type { AccordionTriggerProps, MultipleAccordionProps, SingleAccordionProps };
